import { Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import React from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import HelperText from './HelperText';
import useDeviceSize, { DeviceSize } from '../hooks/useDeviceSize';

export interface SelectOption {
    label: string;
    value: string;
}

type Props = {
    label?: string;
    options: SelectOption[];
    selectedOption: SelectOption | null;
    setSelectedOption: (option: SelectOption | null) => void;
    icon?: React.ReactNode;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    required?: boolean;
}

const StandardSelect = ({ options, selectedOption, setSelectedOption, label, icon, error, helperText, disabled, required }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const device = useDeviceSize();
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const [popperWidth, setPopperWidth] = React.useState(100);

    const handleToggle = () => {
        if (anchorRef && anchorRef.current) {
            setPopperWidth(anchorRef.current.clientWidth);
        }
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }

    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
        }

        prevOpen.current = open;
    }, [open]);

    function handleClick(e: React.MouseEvent<HTMLSelectElement, MouseEvent>) {
        e.preventDefault();
    }

    function handleOptionChange(option: SelectOption) {
        setSelectedOption(option);
        setOpen(false);
    }

    function handleSelectChange(e: React.ChangeEvent<HTMLSelectElement>) {
        const option = options.find(option => option.value === e.target.value);
        if (option) {
            setSelectedOption(option);
        }
    }

    function getBackgroundClassNames() {
        if (disabled) {
            return "bg-utility-muted";
        }
        return "bg-neutral-deep";
    }

    function getButtonColorClassNames() {
        if (disabled) {
            return "bg-primary bg-opacity-40";
        }
        return "bg-primary-dark hover:bg-primary";
    }

    function getSelectClassNames() {
        if (error && open) {
            return "ring-2 ring-error";
        }
        if (error) {
            return "ring-2 ring-red-400";
        }
        if (open) {
            return "ring-2 ring-primary-light";
        }
    }

    return (
        <>
            {device === DeviceSize.Mobile && (
                <>
                    {label && (
                        <label className={`block font-bold text-[16px] text-primary-dark mb-2`}>
                            {label}{required && <span> *</span>}
                        </label>
                    )}
                    <div className="w-full relative">
                        <select
                            className={`${getSelectClassNames()} shadow-sm w-full ${getBackgroundClassNames()} font-bold text-primary-dark placeholder:text-neutral-dark py-[12px] pl-[16px] pr-[16px] rounded-[12px] text-left`}
                            onClick={handleClick}
                            value={selectedOption?.value}
                            onChange={handleSelectChange}
                            disabled={disabled}
                        >
                            {options.map(option => {
                                return <option
                                    key={option.value}
                                    value={option.value}
                                    className="px-10 text-[16px] py-5" style={{ fontFamily: "Inter" }}>
                                    {option.label}
                                </option>
                            })}
                        </select>
                        <button
                            className={`absolute right-[10px] top-[10px] bottom-0 h-[30px] w-[30px] rounded-[8px] flex items-center justify-center pointer-events-none text-primaryDarkSecondary text-white ${getButtonColorClassNames()}`}
                        >
                            {icon && (
                                <>{icon}</>
                            )}
                            {!icon && (
                                <KeyboardArrowDownIcon
                                    style={{ fontSize: "30px" }}
                                />
                            )}
                        </button>
                    </div>
                </>
            )}
            {device === DeviceSize.Desktop && (
                <div>
                    {label && (
                        <label className={`block font-bold text-[16px] text-primary-dark mb-2`}>
                            {label}{required && <span> *</span>}
                        </label>
                    )}
                    <button
                        className={`${getSelectClassNames()} shadow-sm relative ${getBackgroundClassNames()} font-bold text-primary-dark placeholder:text-neutral-dark py-[12px] pl-[16px] pr-[16px] w-full rounded-[12px] text-left`}
                        onClick={handleToggle}
                        ref={anchorRef}
                        disabled={disabled}
                    >
                        {selectedOption ? selectedOption.label : t('common.select')}
                        <div
                            className={`absolute right-[10px] top-[10px] bottom-0 h-[30px] w-[30px] rounded-[8px] flex items-center justify-center pointer-events-none text-primaryDarkSecondary text-white ${getButtonColorClassNames()}`}
                        >
                            {icon && (
                                <>{icon}</>
                            )}
                            {!icon && (
                                <KeyboardArrowDownIcon
                                    style={{ fontSize: "30px" }}
                                />
                            )}
                        </div>
                    </button>
                    <Popper
                        open={open}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        placement="bottom-start"
                        transition
                        disablePortal
                        style={{ zIndex: 1000, textAlign: "center", width: `${popperWidth}px` }}
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === 'bottom-start' ? 'left top' : 'left bottom',
                                    marginLeft: "10px",
                                    marginRight: "10px",
                                }}
                            >
                                <Paper
                                    style={{ maxHeight: "300px", overflowY: "auto", }}
                                >
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList
                                            autoFocusItem={open}
                                            onKeyDown={handleListKeyDown}
                                        >
                                            {options.map(option => {
                                                return <MenuItem
                                                    key={option.value}
                                                    onClick={() => handleOptionChange(option)}
                                                    selected={option.value === selectedOption?.value}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            })}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                    <HelperText error={error}>
                        {helperText}
                    </HelperText>
                </div>
            )}
        </>
    )
}

export default StandardSelect
